// User Settings Data - For Supabase migration
// Contains user preferences, language settings, and app configuration

export const defaultUserSettings = {
  id: "user_settings_default",
  userId: "user_peter",
  username: "Peter",
  displayName: "Peter",
  languageDisplay: "traditional_to_english", // Options: traditional_to_english, simplified_to_english, english_to_traditional, english_to_simplified
  preferredLanguage: "english", // Options: english, traditional_chinese, simplified_chinese
  chineseCharacterPreference: "traditional", // Options: traditional, simplified, mixed
  theme: "light", // Options: light, dark, auto
  pronunciation: {
    enabled: true,
    language: "english", // Options: english, mandarin, cantonese
    autoPlay: false,
    volume: 0.8
  },
  notifications: {
    dailyReminder: true,
    reminderTime: "09:00",
    soundEnabled: true,
    vibrationEnabled: true
  },
  studyPreferences: {
    autoPlayAudio: false,
    showPhonetics: true,
    showImages: true,
    cardAnimation: true,
    studySessionLength: 15 // minutes
  },
  learningSchedule: {
    dailyGoal: 1, // number of sets per day
    preferredTime: "09:00",
    streakGoal: 7, // days
    autoAssign: true // automatically assign sets based on schedule
  },
  progress: {
    streakDays: 7,
    totalWordsLearned: 156,
    setsCompleted: 8,
    accuracy: 85,
    lastStudyDate: new Date().toISOString(),
    completedSets: [],
    favoriteWords: [],
    difficultWords: [],
    calendarData: {} // Format: "YYYY-MM-DD": { status: "DS"|"DF"|"N", setId: "vocab_set_001", score: 85 }
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

// Language display options
export const languageDisplayOptions = [
  {
    value: "traditional_to_english",
    label: "Traditional Chinese → English",
    labelTraditional: "繁體中文 → 英文",
    labelSimplified: "繁体中文 → 英文"
  },
  {
    value: "simplified_to_english", 
    label: "Simplified Chinese → English",
    labelTraditional: "簡體中文 → 英文",
    labelSimplified: "简体中文 → 英文"
  },
  {
    value: "english_to_traditional",
    label: "English → Traditional Chinese",
    labelTraditional: "英文 → 繁體中文",
    labelSimplified: "英文 → 繁体中文"
  },
  {
    value: "english_to_simplified",
    label: "English → Simplified Chinese", 
    labelTraditional: "英文 → 簡體中文",
    labelSimplified: "英文 → 简体中文"
  }
];

// Chinese character preference options
export const chineseCharacterOptions = [
  {
    value: "traditional",
    label: "Traditional Chinese",
    labelTraditional: "繁體中文",
    labelSimplified: "繁体中文"
  },
  {
    value: "simplified",
    label: "Simplified Chinese",
    labelTraditional: "簡體中文",
    labelSimplified: "简体中文"
  },
  {
    value: "mixed",
    label: "Mixed (Original)",
    labelTraditional: "混合（原始）",
    labelSimplified: "混合（原始）"
  }
];

// Pronunciation language options
export const pronunciationOptions = [
  {
    value: "english",
    label: "English",
    labelTraditional: "英文",
    labelSimplified: "英文"
  },
  {
    value: "mandarin",
    label: "Mandarin Chinese",
    labelTraditional: "普通話",
    labelSimplified: "普通话"
  },
  {
    value: "cantonese",
    label: "Cantonese",
    labelTraditional: "粵語",
    labelSimplified: "粤语"
  }
];

// Theme options
export const themeOptions = [
  {
    value: "light",
    label: "Light",
    labelTraditional: "淺色",
    labelSimplified: "浅色"
  },
  {
    value: "dark",
    label: "Dark",
    labelTraditional: "深色",
    labelSimplified: "深色"
  },
  {
    value: "auto",
    label: "Auto",
    labelTraditional: "自動",
    labelSimplified: "自动"
  }
];

// Daily goal options
export const dailyGoalOptions = [
  { value: 1, label: "1 set per day" },
  { value: 2, label: "2 sets per day" },
  { value: 3, label: "3 sets per day" },
  { value: 5, label: "5 sets per day" }
];

// Helper functions
export const getLanguageDisplayLabel = (value, language = "english") => {
  const option = languageDisplayOptions.find(opt => opt.value === value);
  if (!option) return value;
  
  switch (language) {
    case "traditional_chinese":
      return option.labelTraditional;
    case "simplified_chinese":
      return option.labelSimplified;
    default:
      return option.label;
  }
};

export const getPronunciationLabel = (value, language = "english") => {
  const option = pronunciationOptions.find(opt => opt.value === value);
  if (!option) return value;
  
  switch (language) {
    case "traditional_chinese":
      return option.labelTraditional;
    case "simplified_chinese":
      return option.labelSimplified;
    default:
      return option.label;
  }
};

export const getThemeLabel = (value, language = "english") => {
  const option = themeOptions.find(opt => opt.value === value);
  if (!option) return value;
  
  switch (language) {
    case "traditional_chinese":
      return option.labelTraditional;
    case "simplified_chinese":
      return option.labelSimplified;
    default:
      return option.label;
  }
};

export const getChineseCharacterLabel = (value, language = "english") => {
  const option = chineseCharacterOptions.find(opt => opt.value === value);
  if (!option) return value;
  
  switch (language) {
    case "traditional_chinese":
      return option.labelTraditional;
    case "simplified_chinese":
      return option.labelSimplified;
    default:
      return option.label;
  }
};
